/**
 * MinimizedPill — the idle softphone: status dot + "Telefoon", opens the
 * dialpad on click. Container-agnostic (used by both dock variants).
 *
 * Afwezig-staat is hier bewust luid: een agent die zichzelf op afwezig zette
 * (pauze, even weg) en dat vergeet, mist stilletjes alle gesprekken. De pill
 * toont dan een amber banner met één-klik terug naar beschikbaar.
 */

import clsx from 'clsx';
import { useSyncExternalStore } from 'react';
import { usePhoneStore } from '../../../stores/phone-store';
import type { CallRecord, PhoneStatus } from '../../../lib/telephony';
import {
  getPresenceState,
  setPresence,
  subscribePresence,
} from '../../../lib/telephony/presence-store';
import * as phoneState from '../../../lib/telephony/phone-state';
import { NotificationPermissionBanner } from '../NotificationPermissionBanner';
import { PresenceToggle } from '../PresenceToggle';

interface Props {
  status: PhoneStatus;
  activeCall: CallRecord | null;
}

export function MinimizedPill({ status, activeCall }: Props) {
  const setDialPadOpen = usePhoneStore((s) => s.setDialPadOpen);
  const presence = useSyncExternalStore(subscribePresence, getPresenceState);
  const isAway = presence.presence === 'dnd';
  // Melding van de eigenaar-tab (bv. verbinding stil weggevallen en hersteld),
  // gespiegeld naar elke tab via het snapshot; verdwijnt vanzelf na 20 s.
  const notice = useSyncExternalStore(phoneState.subscribe, phoneState.getNotice);

  const statusColor = isAway
    ? 'bg-amber-500'
    : status === 'registered'
      ? 'bg-emerald-500'
      : status === 'connecting'
        ? 'bg-amber-500 animate-pulse'
        : status === 'error'
          ? 'bg-red-500'
          : 'bg-gray-400';
  const statusLabel = isAway
    ? 'afwezig'
    : status === 'registered'
      ? 'verbonden'
      : status === 'connecting'
        ? 'bezig met verbinden'
        : status === 'error'
          ? 'verbindingsfout'
          : 'offline';

  return (
    <div>
      <NotificationPermissionBanner />
      {notice ? (
        <div
          role="status"
          className="mx-2 mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2"
        >
          <p className="text-xs text-amber-800">{notice}</p>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setDialPadOpen(true)}
        aria-label={`Telefoon openen — status: ${statusLabel}`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <span className="flex items-center gap-2">
          <span className={clsx('w-2.5 h-2.5 rounded-full', statusColor)} aria-hidden />
          <span>{isAway ? 'Telefoon — afwezig' : 'Telefoon'}</span>
        </span>
        <span className="text-gray-400 text-xs">{activeCall ? 'Bezet' : 'Ctrl+Shift+D'}</span>
      </button>
      {isAway ? (
        <div className="mx-2 mb-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2">
          <p className="text-xs text-amber-800">
            Je staat op afwezig — inkomende gesprekken rinkelen niet bij jou.
          </p>
          <button
            type="button"
            onClick={() => void setPresence('available')}
            disabled={presence.saving}
            className="mt-1.5 w-full rounded-md bg-emerald-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors"
          >
            Beschikbaar worden
          </button>
        </div>
      ) : (
        <div className="px-2 pb-1.5">
          <PresenceToggle compact />
        </div>
      )}
    </div>
  );
}
